import { useState } from "react";
import { useIsMobile } from "../lib/useIsMobile";
import { getAuthToken, SUPABASE_ANON_KEY, SUPABASE_URL } from "../lib/supabase";
import { AddressAutocomplete } from "./AddressAutocomplete";
import { formatPhoneInput, onlyDigits, isValidPhone, isValidZip, US_STATES, US_TIMEZONES, detectedTz, tzOptionsFor } from "../lib/format";

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/;

// One read-only label/value row (module-level so it's a stable component).
function ProfileRow({ label, value, link }) {
  return (
    <div style={{ display: "flex", justifyContent: "space-between", gap: 16, padding: "11px 0", borderBottom: "1px solid var(--border)" }}>
      <span style={{ fontSize: 12.5, color: "var(--text2)", flexShrink: 0 }}>{label}</span>
      <span style={{ fontSize: 13.5, fontWeight: 500, textAlign: "right", overflowWrap: "anywhere", color: value ? "var(--text)" : "var(--text3)" }}>
        {value ? (link ? <a href={link} target="_blank" rel="noreferrer" style={{ color: "var(--accent)" }}>{value}</a> : value) : "Not set"}
      </span>
    </div>
  );
}

// Settings card for the operator's own business profile. READ-ONLY by default —
// the fields only become editable after an explicit "Edit" (show of intent) —
// then Save/Cancel. Writes to organizations via the org_self_update RLS policy.
export function BusinessProfileEditor({ org, showToast, onSaved }) {
  const isMobile = useIsMobile();
  const seed = () => ({
    name:         org?.name || "",
    contact_name: org?.contact_name || "",
    phone:        org?.phone || "",
    address:      org?.address || "",
    city:         org?.city || "",
    state:        org?.state || "",
    zip:          org?.zip || "",
    timezone:     org?.timezone || detectedTz(),
    website:      org?.website || "",
    reply_to_email: org?.reply_to_email || "",
  });
  const [f, setF] = useState(seed);
  const [editing, setEditing] = useState(false);
  const [saving, setSaving] = useState(false);

  async function save() {
    if (!f.name.trim())         { showToast("Company name is required", "error"); return; }
    if (!isValidPhone(f.phone)) { showToast("Phone should be 10 digits", "error"); return; }
    if (!isValidZip(f.zip))     { showToast("ZIP should be 5 digits", "error"); return; }
    // Optional, but a typo here silently sends every client reply into the void —
    // the emails themselves keep sending, so nothing would look broken.
    if (f.reply_to_email.trim() && !EMAIL_RE.test(f.reply_to_email.trim())) {
      showToast("Reply-to email doesn't look like a valid address", "error"); return;
    }
    setSaving(true);
    try {
      const r = await fetch(`${SUPABASE_URL}/rest/v1/organizations?id=eq.${org.id}`, {
        method: "PATCH",
        headers: { apikey: SUPABASE_ANON_KEY, Authorization: `Bearer ${getAuthToken()}`, "Content-Type": "application/json", Prefer: "return=minimal" },
        body: JSON.stringify({
          name: f.name.trim(),
          contact_name: f.contact_name.trim() || null,
          phone:   f.phone.trim()   || null,
          address: f.address.trim() || null,
          city:    f.city.trim()    || null,
          state:   f.state || null,
          zip:     f.zip.trim()     || null,
          timezone: f.timezone || null,
          website: f.website.trim() ? (/^https?:\/\//i.test(f.website.trim()) ? f.website.trim() : "https://" + f.website.trim()) : null,
          reply_to_email: f.reply_to_email.trim() || null,
        }),
      });
      if (!r.ok) throw new Error("save failed");
      showToast("Business profile saved");
      setEditing(false);
      onSaved && onSaved();
    } catch (e) { console.error(e); showToast("Could not save business profile", "error"); }
    setSaving(false);
  }

  const inp = { width: "100%", padding: "9px 11px", border: "1px solid var(--border)", borderRadius: "var(--r-sm, 8px)", fontSize: 13, boxSizing: "border-box", background: "var(--surface)", color: "var(--text)" };
  const lbl = { display: "block", fontSize: 11, fontWeight: 600, color: "var(--text2)", textTransform: "uppercase", letterSpacing: ".04em", marginBottom: 4 };
  const set = (k, v) => setF(p => ({ ...p, [k]: v }));

  if (!org) return null;

  const tzLabel  = (US_TIMEZONES.find(([v]) => v === f.timezone) || [f.timezone, f.timezone])[1];
  const fullAddr = [f.address, [f.city, f.state].filter(Boolean).join(", "), f.zip].filter(Boolean).join("  ·  ");

  return (
    <div className="card">
      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 10 }}>
        <div className="card-title" style={{ margin: 0 }}>🏢 Business Profile</div>
        {!editing && <button className="btn btn-ghost btn-sm" onClick={() => setEditing(true)}>✏️ Edit</button>}
      </div>
      <div style={{ fontSize: 12, color: "var(--text2)", margin: "4px 0 14px" }}>Appears on invoices, receipts, and your clients' texts.</div>

      {!editing ? (
        <div>
          <ProfileRow label="Company name" value={f.name} />
          <ProfileRow label="Your name"    value={f.contact_name} />
          <ProfileRow label="Phone"        value={f.phone ? formatPhoneInput(f.phone) : ""} />
          <ProfileRow label="Address"      value={fullAddr} />
          <ProfileRow label="Timezone"     value={f.timezone ? tzLabel : ""} />
          <ProfileRow label="Website"      value={f.website} link={f.website} />
          <ProfileRow label="Client replies to" value={f.reply_to_email} />
        </div>
      ) : (
        <>
          {/* Phones: one column — two 1fr columns inside the settings pane were ~124px each. */}
          <div style={{ display: "grid", gridTemplateColumns: isMobile ? "minmax(0,1fr)" : "1fr 1fr", gap: 12 }}>
            <div style={{ gridColumn: "1 / -1" }}><label style={lbl}>Company name</label><input style={inp} value={f.name} onChange={e => set("name", e.target.value)} /></div>
            <div><label style={lbl}>Your name</label><input style={inp} value={f.contact_name} onChange={e => set("contact_name", e.target.value)} /></div>
            <div><label style={lbl}>Phone</label><input style={inp} value={f.phone} onChange={e => set("phone", formatPhoneInput(e.target.value))} inputMode="tel" placeholder="555-555-5555" /></div>
            <div style={{ gridColumn: "1 / -1" }}>
              <label style={lbl}>Address</label>
              <AddressAutocomplete
                value={f.address}
                onChange={v => set("address", v)}
                onPick={p => setF(prev => ({ ...prev, address: p.address, city: p.city || prev.city, state: p.state || prev.state, zip: p.zip || prev.zip }))}
                style={inp}
                placeholder="Start typing your business address…"
                confirmed={!!f.zip}
              />
            </div>
            <div><label style={lbl}>City</label><input style={inp} value={f.city} onChange={e => set("city", e.target.value)} /></div>
            <div style={{ display: "flex", gap: 10, minWidth: 0 }}>
              {/* Phones: a 2-letter state doesn't need half the row — give ZIP the rest. */}
              <div style={isMobile ? { flex: "0 0 110px" } : { flex: 1 }}>
                <label style={lbl}>State</label>
                <select style={{ ...inp, appearance: "auto" }} value={f.state} onChange={e => set("state", e.target.value)}>
                  <option value="">—</option>
                  {US_STATES.map(s => <option key={s} value={s}>{s}</option>)}
                </select>
              </div>
              <div style={{ flex: 1, minWidth: 0 }}><label style={lbl}>ZIP</label><input style={inp} value={f.zip} onChange={e => set("zip", onlyDigits(e.target.value, 5))} inputMode="numeric" placeholder="12345" /></div>
            </div>
            <div style={{ gridColumn: "1 / -1" }}>
              <label style={lbl}>Timezone</label>
              <select style={{ ...inp, appearance: "auto" }} value={f.timezone} onChange={e => set("timezone", e.target.value)}>
                {tzOptionsFor(f.timezone).map(([v, l]) => <option key={v} value={v}>{l}</option>)}
              </select>
            </div>
            <div style={{ gridColumn: "1 / -1" }}><label style={lbl}>Website</label><input style={inp} value={f.website} onChange={e => set("website", e.target.value)} inputMode="url" placeholder="yourbusiness.com" /></div>
            {/* Invoices and receipts send from the platform's address, which can't
                receive mail. Without this, a client who hits Reply gets a bounce. */}
            <div style={{ gridColumn: "1 / -1" }}>
              <label style={lbl}>Client replies to</label>
              <input style={inp} value={f.reply_to_email} onChange={e => set("reply_to_email", e.target.value)} inputMode="email" type="email" placeholder="you@yourbusiness.com" />
              <div style={{ fontSize: 11, color: "var(--text2)", marginTop: 4 }}>
                Where replies land when a client answers an invoice, receipt, or payment link. Leave blank and replies bounce.
              </div>
            </div>
          </div>
          <div style={{ display: "flex", gap: 8, marginTop: 14, flexWrap: "wrap" }}>
            <button className="btn btn-gold" disabled={saving} onClick={save}>{saving ? "Saving..." : "Save changes"}</button>
            <button className="btn btn-ghost" disabled={saving} onClick={() => { setF(seed()); setEditing(false); }}>Cancel</button>
          </div>
        </>
      )}
    </div>
  );
}
